import { PromoCampaignParams } from '@analytics/const';
import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { StatePersistenceService } from '@spartacus/core';
import { Observable, Subscription, fromEvent } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

/**
 * Данный сервис используется для хранения метаданных
 * рекламной кампании.
 *
 * Если пользователь перешел по рекламной ссылке, то
 * есть по ссылке у которой есть метаданные рекламной
 * кампании, то их необходимо сохранять.
 *
 * Время жизни метаданных задается параметром ttl. При
 * взаимодействии пользователя со страницей ttl обновляется.
 *
 * В момент оформления заказа передавать данные на бекенд.
 */

@Injectable({
  providedIn: 'root',
})
export class PromoCampaignService implements OnDestroy {
  private readonly subscription = new Subscription();
  /**
   * Наименование ключа для хранения данных в локалСторедж
   */
  private readonly key = 'promoCampaign';
  /**
   * Период хранения данных в милисекундах,
   * после которого они становятся не действительными
   *
   * Если пользователь не производит никаких действий
   * на сайте в течении 30 минут, то данные становятся
   * не действительными
   */
  private readonly ttl = 30 * 60 * 1000;
  /**
   * Промо параметры рекламной кампании
   */
  private _promoCampaignParams: PromoCampaignParams | undefined;

  constructor(
    private statePersistenceService: StatePersistenceService,
    private location: Location,
    @Inject(DOCUMENT) private document: any,
  ) {}

  /**
   * Инициализация происходит только если рекламная
   * сессия еще не протухла
   */
  init(): void {
    this._promoCampaignParams = this.getInitialPromoCampaignParams();

    if (!this._promoCampaignParams) {
      return;
    }

    this.subscription.add(
      this.statePersistenceService.syncWithStorage({
        key: this.key,
        state$: this.getState(),
      }),
    );
  }

  get promoCampaignParams(): PromoCampaignParams | undefined {
    if (this._promoCampaignParams && this.isValid(this._promoCampaignParams)) {
      return {
        utmSource: this._promoCampaignParams.utmSource,
        utmMedium: this._promoCampaignParams.utmMedium,
      };
    }

    return undefined;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getState(): Observable<PromoCampaignParams | undefined> {
    return this.onUserInteracts().pipe(
      map(() => {
        if (this._promoCampaignParams && this.isValid(this._promoCampaignParams)) {
          this._promoCampaignParams = {
            ...this._promoCampaignParams,
            updatedAt: Date.now(),
          };

          return this._promoCampaignParams;
        }

        /**
         * Если рекламная сессия протухла,
         * то отписываемся от всех подписок
         */
        this.ngOnDestroy();

        return undefined;
      }),
    );
  }

  /**
   * Эмитит событие каждый раз когда пользователь взаимодействует с сайтом
   * Под активностью подразумевается клик в любом месте сайта
   */
  private onUserInteracts(): Observable<void> {
    return fromEvent(this.document, 'click').pipe(
      debounceTime(1000),
      startWith(true),
      map(() => undefined),
    );
  }

  /**
   * Получить метки рекламной кампании из УРЛа или из локалСтореджа
   * @returns рекламные метки
   */
  private getInitialPromoCampaignParams(): PromoCampaignParams | undefined {
    const queryParams = new URLSearchParams(this.location.path().split('?')[1]);

    if (queryParams.has('utm_source') && queryParams.has('utm_medium')) {
      return {
        utmSource: queryParams.get('utm_source') as string,
        utmMedium: queryParams.get('utm_medium') as string,
        updatedAt: Date.now(),
      };
    }

    const stateFromStorage = this.statePersistenceService.readStateFromStorage<PromoCampaignParams>({ key: this.key });

    if (stateFromStorage && this.isValid(stateFromStorage)) {
      return stateFromStorage;
    }

    return undefined;
  }

  private isValid(promoCampaignParams: PromoCampaignParams): boolean {
    return !!promoCampaignParams.updatedAt && Date.now() - promoCampaignParams.updatedAt <= this.ttl;
  }
}
